import { useState, useRef, useCallback, useEffect } from 'react'
import { 
  Mail, Clock, GitBranch, MousePointer2, ZoomIn, ZoomOut, 
  Maximize2, Minimize2, Trash2, Play, X, Eye, Variable,
  Undo, Redo, ArrowLeft, Focus, Loader2, Check, Save,
  FileText, Upload, Link, UserMinus
} from 'lucide-react'
import toast from 'react-hot-toast'
import { 
  createEmailSequence, updateEmailSequence, getSequenceEnrollments,
  getSequenceEnrollmentStats, getSequenceDebugInfo, getEnrollmentDebugStatus, executeEnrollmentStep,
  resetEnrollmentTracking, runAllSequenceEnrollments, enrollProspectInSequence,
  removeEnrollmentFromSequence, resetEnrollment, updateEnrollmentAdmin
} from '../../services/api'

// CSS pour l'animation de spin
const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`
if (typeof document !== 'undefined' && !document.getElementById('spin-keyframes')) {
  const style = document.createElement('style')
  style.id = 'spin-keyframes'
  style.textContent = spinKeyframes
  document.head.appendChild(style)
}

const NODE_TYPES = {
  trigger: { type: 'trigger', label: 'Déclencheur', icon: Play, color: '#10b981' },
  email: { type: 'email', label: 'Email', icon: Mail, color: '#3b82f6' },
  delay: { type: 'delay', label: 'Délai', icon: Clock, color: '#f59e0b' },
  condition: { type: 'condition', label: 'Condition', icon: GitBranch, color: '#8b5cf6' }
}

const MERGE_VARIABLES = [
  { key: 'firstName', label: 'Prénom' }, { key: 'lastName', label: 'Nom' },
  { key: 'companyName', label: 'Entreprise' }, { key: 'jobTitle', label: 'Poste' },
]

const DEFAULT_NODES = [
  { id: 'trigger-1', type: 'trigger', position: { x: 100, y: 150 }, data: { label: 'Nouveau prospect' } }
]

const DEFAULT_EDGES = []

const isEditableElement = (element) => {
  if (!element) return false
  const tag = element.tagName
  return element.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

const normalizeWorkflowData = (workflowData) => {
  if (!workflowData || typeof workflowData !== 'object') {
    return { nodes: DEFAULT_NODES, edges: DEFAULT_EDGES, settings: {} }
  }

  const nodes = Array.isArray(workflowData.nodes) ? workflowData.nodes : DEFAULT_NODES
  const settings = typeof workflowData.settings === 'object' && workflowData.settings ? workflowData.settings : {}

  if (workflowData.connections && typeof workflowData.connections === 'object') {
    return { nodes, edges: connectionsToEdges(nodes, workflowData.connections), settings }
  }

  const edges = Array.isArray(workflowData.edges) ? workflowData.edges : DEFAULT_EDGES
  return { nodes, edges, settings }
}

const connectionsToEdges = (nodes, connections) => {
  const nodesById = Object.fromEntries(nodes.map((node) => [node.id, node]))
  const edges = []

  Object.entries(connections || {}).forEach(([sourceId, connectionTypes]) => {
    const mainConnections = connectionTypes?.main
    if (!Array.isArray(mainConnections)) return

    mainConnections.forEach((targets, outputIndex) => {
      if (!Array.isArray(targets)) return
      targets.forEach((target, index) => {
        if (!target?.node) return
        const edge = {
          id: `e-${sourceId}-${target.node}-${outputIndex}-${index}`,
          source: sourceId,
          target: target.node,
        }
        if (nodesById[sourceId]?.type === 'condition') {
          if (outputIndex === 0) {
            edge.label = 'Oui'
            edge.sourceHandle = 'yes'
          } else if (outputIndex === 1) {
            edge.label = 'Non'
            edge.sourceHandle = 'no'
          }
        }
        edges.push(edge)
      })
    })
  })

  return edges
}

const edgesToConnections = (nodes, edges) => {
  const nodesById = Object.fromEntries(nodes.map((node) => [node.id, node]))
  const connections = {}

  edges.forEach((edge) => {
    if (!edge?.source || !edge?.target) return
    let outputIndex = 0
    if (nodesById[edge.source]?.type === 'condition') {
      const label = (edge.label || '').toLowerCase()
      const handle = (edge.sourceHandle || '').toLowerCase()
      if (['no', 'non'].includes(label) || ['no', 'non'].includes(handle)) {
        outputIndex = 1
      }
    }

    connections[edge.source] = connections[edge.source] || { main: [] }
    if (!Array.isArray(connections[edge.source].main[outputIndex])) {
      connections[edge.source].main[outputIndex] = []
    }
    connections[edge.source].main[outputIndex].push({
      node: edge.target,
      type: 'main',
      index: 0
    })
  })

  return connections
}

export default function SequenceWorkflowBuilder({ sequence = null, onSave = null }) {
  const canvasRef = useRef(null)
  
  // Initialiser depuis la séquence existante ou les valeurs par défaut
  const normalizedWorkflow = normalizeWorkflowData(sequence?.workflowData)
  const initialNodes = normalizedWorkflow.nodes
  const initialEdges = normalizedWorkflow.edges
  const initialSettings = normalizedWorkflow.settings
  
  const [nodes, setNodes] = useState(initialNodes)
  const [edges, setEdges] = useState(initialEdges)
  const [workflowSettings] = useState(initialSettings)
  const [sequenceName, setSequenceName] = useState(sequence?.name || '')
  const [sequenceId, setSequenceId] = useState(sequence?.id || null)
  const [selectedNodes, setSelectedNodes] = useState(new Set())
  const [selectedEdge, setSelectedEdge] = useState(null)
  const [draggingNode, setDraggingNode] = useState(null)
  const [draggingEdge, setDraggingEdge] = useState(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [selectionBox, setSelectionBox] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  const [editingNode, setEditingNode] = useState(null)
  const [connecting, setConnecting] = useState(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
  const [history, setHistory] = useState({ past: [], future: [] })
  const [isFullscreen, setIsFullscreen] = useState(false)
  const containerRef = useRef(null)
  const [hoveredNode, setHoveredNode] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showJsonImport, setShowJsonImport] = useState(false)
  const [jsonInput, setJsonInput] = useState('')
  
  // Debug panel state (pre-prod only)
  const [showDebugPanel, setShowDebugPanel] = useState(false)
  const [debugData, setDebugData] = useState(null)
  const [debugStats, setDebugStats] = useState(null)
  const [debugLoading, setDebugLoading] = useState(false)
  const [enrollments, setEnrollments] = useState([])
  const [enrollmentsTotal, setEnrollmentsTotal] = useState(0)
  const [enrollmentsHasMore, setEnrollmentsHasMore] = useState(false)
  const [debugFilters, setDebugFilters] = useState({ status: '', q: '', page: 1, limit: 50 })
  const [selectedEnrollment, setSelectedEnrollment] = useState(null)
  const [adminStatus, setAdminStatus] = useState('')
  const [adminCurrentStep, setAdminCurrentStep] = useState('')
  const [adminResetCounters, setAdminResetCounters] = useState(false)
  const [adminResetHistory, setAdminResetHistory] = useState(false)
  const [enrollProspectId, setEnrollProspectId] = useState('')
  const [enrollForceReset, setEnrollForceReset] = useState(false)

  const handleSaveWorkflow = async () => {
    if (!sequenceName.trim()) {
      toast.error('Veuillez donner un nom à la séquence')
      return
    }
    
    setIsSaving(true)
    setSaveSuccess(false)
    
    try {
      const sanitizedNodes = nodes.map((node) => {
        if (node.type !== 'email') return node
        const data = { ...(node.data || {}) }
        delete data.trackOpens
        delete data.trackClicks
        delete data.useSignature
        return { ...node, data }
      })
      const workflowData = { 
        nodes: sanitizedNodes, 
        connections: edgesToConnections(sanitizedNodes, edges),
        settings: workflowSettings
      }
      const payload = { 
        name: sequenceName.trim(), 
        workflowData,
        status: 'draft'
      }
      
      let result
      if (sequenceId) {
        // Mise à jour
        result = await updateEmailSequence(sequenceId, payload)
        toast.success('Séquence mise à jour')
      } else {
        // Création
        result = await createEmailSequence(payload)
        setSequenceId(result.id)
        toast.success('Séquence créée')
      }
      
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
      
      if (onSave) onSave(result)
    } catch (error) {
      console.error('Save error:', error)
      toast.error(error.message || 'Erreur lors de la sauvegarde')
    } finally {
      setIsSaving(false)
    }
  }

  const buildClipboardPayload = (selectedIds) => {
    const nodeList = nodes.filter((node) => selectedIds.has(node.id))
    const edgeList = edges.filter((edge) => selectedIds.has(edge.source) && selectedIds.has(edge.target))
    return {
      nodes: nodeList,
      connections: edgesToConnections(nodeList, edgeList)
    }
  }

  const handleCopySelection = async () => {
    const hasSelection = selectedNodes.size > 0
    const selectedIds = hasSelection ? selectedNodes : new Set(nodes.map((node) => node.id))
    if (selectedIds.size === 0) {
      toast.error('Aucun node à copier')
      return
    }
    const payload = buildClipboardPayload(selectedIds)
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
      toast.success(hasSelection ? 'Sélection copiée' : 'Workflow copié')
    } catch (error) {
      console.error('Clipboard copy error:', error)
      toast.error('Copie impossible')
    }
  }

  const handlePasteSelection = async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (!text) return
      const parsed = JSON.parse(text)
      if (!parsed || !Array.isArray(parsed.nodes)) {
        toast.error('JSON invalide pour collage')
        return
      }
      let importedEdges = []
      if (parsed.connections && typeof parsed.connections === 'object') {
        importedEdges = connectionsToEdges(parsed.nodes, parsed.connections)
      } else if (Array.isArray(parsed.edges)) {
        importedEdges = parsed.edges
      }

      const offset = { x: 40, y: 40 }
      const existingIds = new Set(nodes.map((node) => node.id))
      const idMap = {}

      const newNodes = parsed.nodes.map((node, index) => {
        const baseId = node.id || `node-${Date.now()}-${index}`
        let newId = baseId
        let suffix = 0
        while (existingIds.has(newId) || idMap[newId]) {
          suffix += 1
          newId = `${baseId}-${suffix}`
        }
        idMap[node.id] = newId
        const pos = node.position || { x: 0, y: 0 }
        return {
          ...node,
          id: newId,
          position: { x: pos.x + offset.x, y: pos.y + offset.y }
        }
      })

      const newEdges = importedEdges
        .map((edge, index) => {
          const source = idMap[edge.source]
          const target = idMap[edge.target]
          if (!source || !target) return null
          return {
            ...edge,
            id: `e-${source}-${target}-${Date.now()}-${index}`,
            source,
            target
          }
        })
        .filter(Boolean)

      saveHistory()
      setNodes((prev) => [...prev, ...newNodes])
      setEdges((prev) => [...prev, ...newEdges])
      setSelectedNodes(new Set(newNodes.map((node) => node.id)))
      setSelectedEdge(null)
      toast.success('Nodes collés')
    } catch (error) {
      console.error('Clipboard paste error:', error)
      toast.error('Collage impossible')
    }
  }

  const handleJsonImport = () => {
    if (!jsonInput.trim()) {
      toast.error('Veuillez saisir du JSON valide')
      return
    }

    try {
      const workflowData = JSON.parse(jsonInput)
      
      if (!workflowData.nodes || !Array.isArray(workflowData.nodes)) {
        toast.error('Le JSON doit contenir un array "nodes"')
        return
      }
      
      let importedEdges = []
      if (workflowData.connections && typeof workflowData.connections === 'object') {
        importedEdges = connectionsToEdges(workflowData.nodes, workflowData.connections)
      } else if (Array.isArray(workflowData.edges)) {
        importedEdges = workflowData.edges
      } else {
        toast.error('Le JSON doit contenir "edges" ou "connections"')
        return
      }

      // Validation des nodes
      const validNodes = workflowData.nodes.filter(node => 
        node.id && node.type && node.position && 
        typeof node.position.x === 'number' && typeof node.position.y === 'number'
      )
      
      if (validNodes.length !== workflowData.nodes.length) {
        toast.error(`${workflowData.nodes.length - validNodes.length} nodes invalides ignorés`)
      }

      // Validation des edges
      const nodeIds = new Set(validNodes.map(n => n.id))
      const validEdges = importedEdges.filter(edge => 
        edge.source && edge.target && nodeIds.has(edge.source) && nodeIds.has(edge.target)
      )
      
      if (validEdges.length !== importedEdges.length) {
        toast.error(`${importedEdges.length - validEdges.length} edges invalides ignorés`)
      }

      // Sauvegarder l'état actuel pour l'historique
      saveHistory()
      
      // Appliquer les nouvelles données
      setNodes(validNodes)
      setEdges(validEdges)
      
      toast.success(`Workflow importé: ${validNodes.length} nodes, ${validEdges.length} edges`)
      setShowJsonImport(false)
      setJsonInput('')
      
      // Fit to view après un court délai
      setTimeout(() => fitToView(), 100)
      
    } catch (error) {
      console.error('JSON parse error:', error)
      toast.error('JSON invalide: ' + error.message)
    }
  }

  const getExampleJson = () => {
    return JSON.stringify({
      "nodes": [
        {
          "id": "trigger-1",
          "type": "trigger",
          "position": { "x": 100, "y": 150 },
          "data": { 
            "label": "Nouveau prospect" 
          }
        },
        {
          "id": "email-1",
          "type": "email",
          "position": { "x": 300, "y": 150 },
          "data": {
            "label": "Premier contact",
            "subject": "Premier contact - {{firstName}}",
            "body": "Bonjour {{firstName}},\n\nJe me permets de vous contacter concernant votre profil qui a retenu mon attention.\n\nJ'aimerais échanger avec vous sur les opportunités de collaboration.\n\nCordialement"
          }
        },
        {
          "id": "delay-1",
          "type": "delay",
          "position": { "x": 500, "y": 150 },
          "data": { 
            "label": "Délai 1 jour",
            "delayValue": 1, 
            "delayUnit": "days" 
          }
        },
        {
          "id": "condition-1",
          "type": "condition",
          "position": { "x": 700, "y": 150 },
          "data": { 
            "label": "A ouvert?",
            "conditionType": "email_opened" 
          }
        },
        {
          "id": "email-yes",
          "type": "email",
          "position": { "x": 900, "y": 80 },
          "data": {
            "label": "Suivi - Ouvert",
            "subject": "Merci {{firstName}} !",
            "body": "Bonjour {{firstName}},\n\nJe vois que vous avez consulté mon message précédent, c'est parfait !\n\nSouhaitez-vous que nous planifions un appel cette semaine ?\n\nCordialement"
          }
        },
        {
          "id": "email-no",
          "type": "email",
          "position": { "x": 900, "y": 220 },
          "data": {
            "label": "Relance",
            "subject": "Re: Premier contact - {{firstName}}",
            "body": "Bonjour {{firstName}},\n\nJe me permets de relancer suite à mon message précédent.\n\nPeut-être êtes-vous occupé(e) en cette période ?\n\nN'hésitez pas à me faire savoir si le timing n'est pas idéal.\n\nCordialement"
          }
        }
      ],
      "connections": {
        "trigger-1": {
          "main": [[{ "node": "email-1", "type": "main", "index": 0 }]]
        },
        "email-1": {
          "main": [[{ "node": "delay-1", "type": "main", "index": 0 }]]
        },
        "delay-1": {
          "main": [[{ "node": "condition-1", "type": "main", "index": 0 }]]
        },
        "condition-1": {
          "main": [
            [{ "node": "email-yes", "type": "main", "index": 0 }],
            [{ "node": "email-no", "type": "main", "index": 0 }]
          ]
        }
      },
      "settings": {}
    }, null, 2)
  }

  // Debug functions (pre-prod only)
  const loadDebugData = async (overrides = {}) => {
    if (!sequenceId) {
      toast.error('Sauvegardez d\'abord la séquence')
      return
    }
    const nextFilters = { ...debugFilters, ...overrides }
    setDebugFilters(nextFilters)
    setDebugLoading(true)
    try {
      const [debugInfo, stats, enrollmentList] = await Promise.all([
        getSequenceDebugInfo(sequenceId),
        getSequenceEnrollmentStats(sequenceId),
        getSequenceEnrollments(sequenceId, nextFilters)
      ])
      const items = enrollmentList?.items || enrollmentList?.enrollments || enrollmentList?.['hydra:member'] || enrollmentList?.member || []
      setDebugData(debugInfo)
      setDebugStats(stats)
      setEnrollments(items)
      setEnrollmentsTotal(enrollmentList?.total ?? items.length)
      setEnrollmentsHasMore(Boolean(enrollmentList?.hasMore))
      setShowDebugPanel(true)
    } catch (error) {
      console.error('Debug load error:', error)
      toast.error('Erreur chargement diagnostic: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const loadEnrollmentDebug = async (enrollmentId) => {
    setDebugLoading(true)
    try {
      const data = await getEnrollmentDebugStatus(enrollmentId)
      setSelectedEnrollment(data)
    } catch (error) {
      console.error('Enrollment debug error:', error)
      toast.error('Erreur: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const handleExecuteStep = async (enrollmentId) => {
    setDebugLoading(true)
    try {
      const result = await executeEnrollmentStep(enrollmentId)
      toast.success('Step exécuté: ' + (result.execution_result?.action || 'OK'))
      await loadEnrollmentDebug(enrollmentId)
    } catch (error) {
      toast.error('Erreur: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const handleResetTracking = async (enrollmentId) => {
    setDebugLoading(true)
    try {
      await resetEnrollmentTracking(enrollmentId)
      toast.success('Compteurs réinitialisés')
      await loadEnrollmentDebug(enrollmentId)
    } catch (error) {
      toast.error('Erreur: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const handleResetEnrollment = async (enrollmentId) => {
    setDebugLoading(true)
    try {
      await resetEnrollment(enrollmentId)
      toast.success('Enrollment réinitialisé')
      await loadEnrollmentDebug(enrollmentId)
      await loadDebugData({ page: debugFilters.page })
    } catch (error) {
      toast.error('Erreur: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const handleRemoveEnrollment = async (enrollmentId) => {
    if (!sequenceId) return
    if (!confirm('Supprimer cet enrollment ?')) return
    setDebugLoading(true)
    try {
      await removeEnrollmentFromSequence(sequenceId, enrollmentId)
      toast.success('Enrollment supprimé')
      setSelectedEnrollment(null)
      await loadDebugData({ page: Math.max(1, debugFilters.page) })
    } catch (error) {
      toast.error('Erreur: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const handleAdminUpdate = async () => {
    if (!selectedEnrollment?.enrollment_id) return
    setDebugLoading(true)
    try {
      const payload = {
        status: adminStatus || undefined,
        currentStep: adminCurrentStep !== '' ? Number(adminCurrentStep) : undefined,
        resetCounters: adminResetCounters,
        resetHistory: adminResetHistory
      }
      await updateEnrollmentAdmin(selectedEnrollment.enrollment_id, payload)
      toast.success('Enrollment mis à jour')
      await loadEnrollmentDebug(selectedEnrollment.enrollment_id)
      await loadDebugData({ page: debugFilters.page })
    } catch (error) {
      toast.error('Erreur: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const handleEnrollProspect = async () => {
    if (!sequenceId || !enrollProspectId.trim()) return
    setDebugLoading(true)
    try {
      const prospectId = Number(enrollProspectId)
      if (Number.isNaN(prospectId)) {
        toast.error('Prospect ID invalide')
        return
      }
      await enrollProspectInSequence(sequenceId, prospectId, enrollForceReset)
      toast.success('Prospect inscrit')
      setEnrollProspectId('')
      await loadDebugData({ page: 1 })
    } catch (error) {
      toast.error('Erreur: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const handleRunAll = async () => {
    if (!sequenceId) return
    setDebugLoading(true)
    try {
      const result = await runAllSequenceEnrollments(sequenceId)
      toast.success(`${result.processed || 0} enrollments traités`)
      await loadDebugData()
    } catch (error) {
      toast.error('Erreur: ' + (error.response?.data?.error || error.message))
    } finally {
      setDebugLoading(false)
    }
  }

  const refreshDebugStats = async () => {
    if (!sequenceId) return
    try {
      const stats = await getSequenceEnrollmentStats(sequenceId)
      setDebugStats(stats)
    } catch (error) {
      console.error('Debug stats refresh error:', error)
    }
  }

  useEffect(() => {
    if (!showDebugPanel) return
    const interval = setInterval(() => {
      refreshDebugStats()
    }, 30000)
    return () => clearInterval(interval)
  }, [showDebugPanel, sequenceId])

  useEffect(() => {
    if (!selectedEnrollment) {
      setAdminStatus('')
      setAdminCurrentStep('')
      setAdminResetCounters(false)
      setAdminResetHistory(false)
      return
    }
    setAdminStatus(selectedEnrollment.enrollment_status || selectedEnrollment.status || '')
    setAdminCurrentStep(
      selectedEnrollment.current_step !== undefined ? String(selectedEnrollment.current_step) : ''
    )
    setAdminResetCounters(false)
    setAdminResetHistory(false)
  }, [selectedEnrollment])

  // Sync fullscreen state
  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])

  // Gestion de l'historique
  const saveHistory = useCallback(() => {
    setHistory(curr => ({
      past: [...curr.past.slice(-19), { nodes: [...nodes], edges: [...edges] }], // Garde les 20 derniers états
      future: []
    }))
  }, [nodes, edges])

  const undo = useCallback(() => {
    setHistory(curr => {
      if (curr.past.length === 0) return curr
      const previous = curr.past[curr.past.length - 1]
      const newPast = curr.past.slice(0, -1)
      return {
        past: newPast,
        future: [ { nodes: [...nodes], edges: [...edges] }, ...curr.future ],
        present: previous // Juste pour info, on utilise setNodes direct
      }
    })
  }, [nodes, edges])

  // Application de l'état undo/redo (séparé pour éviter les boucles)
  useEffect(() => {
    const lastAction = history.present
    if (lastAction) {
       setNodes(lastAction.nodes)
       setEdges(lastAction.edges)
       // Nettoyage du flag 'present' dans l'historique
       setHistory(h => { const { present, ...rest } = h; return rest })
    }
  }, [history])
  
  const performUndo = () => {
    if (history.past.length === 0) return
    const previous = history.past[history.past.length - 1]
    setHistory(h => ({
      past: h.past.slice(0, -1),
      future: [ { nodes, edges }, ...h.future ]
    }))
    setNodes(previous.nodes)
    setEdges(previous.edges)
  }

  const performRedo = () => {
    if (history.future.length === 0) return
    const next = history.future[0]
    setHistory(h => ({
      past: [ { nodes, edges }, ...h.past ],
      future: h.future.slice(1)
    }))
    setNodes(next.nodes)
    setEdges(next.edges)
  }

  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeElement = typeof document !== 'undefined' ? document.activeElement : null
      if (editingNode || isEditableElement(e.target) || isEditableElement(activeElement)) {
        return
      }
      // Undo / Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault()
        if (e.shiftKey) performRedo()
        else performUndo()
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
        e.preventDefault()
        handleCopySelection()
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault()
        handlePasteSelection()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault()
        performRedo()
      }
      // Suppression (Delete ou Backspace)
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodes.size > 0 || selectedEdge) {
          e.preventDefault()
          saveHistory()
          if (selectedNodes.size > 0) {
            const toDelete = Array.from(selectedNodes)
            setNodes(curr => curr.filter(n => !selectedNodes.has(n.id)))
            setEdges(curr => curr.filter(edge => !selectedNodes.has(edge.source) && !selectedNodes.has(edge.target)))
            setSelectedNodes(new Set())
          }
          if (selectedEdge) {
            setEdges(curr => curr.filter(edge => edge.id !== selectedEdge))
            setSelectedEdge(null)
          }
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nodes, edges, history, selectedNodes, selectedEdge, editingNode, handleCopySelection, handlePasteSelection])

  const GRID_SIZE = 20
  const snapPos = (p) => ({ x: Math.round(p.x / GRID_SIZE) * GRID_SIZE, y: Math.round(p.y / GRID_SIZE) * GRID_SIZE })

  const handleDragStart = (e, t) => { e.dataTransfer.setData('nodeType', t); e.dataTransfer.effectAllowed = 'copy' }
  
  const handleDrop = (e) => {
    e.preventDefault()
    const nodeType = e.dataTransfer.getData('nodeType')
    if (!nodeType || !NODE_TYPES[nodeType]) return
    saveHistory() // Save before adding
    const rect = canvasRef.current.getBoundingClientRect()
    const pos = snapPos({ x: (e.clientX - rect.left - pan.x) / zoom, y: (e.clientY - rect.top - pan.y) / zoom })
    const newNode = { id: `${nodeType}-${Date.now()}`, type: nodeType, position: pos, data: getDefaultData(nodeType), width: 220, height: 100 }
    setNodes(prev => [...prev, newNode])
  }

  const getDefaultData = (t) => {
    if (t === 'trigger') return { label: 'Déclencheur' }
    if (t === 'email') return { label: 'Nouvel email', subject: '', body: '' }
    if (t === 'delay') return { label: 'Délai', delayValue: 1, delayUnit: 'days' }
    if (t === 'condition') return { label: 'Condition', conditionType: 'email_opened' }
    return { label: 'Node' }
  }

  // Dimensions fixes : trigger = carré, autres = rectangulaires
  const getNodeSize = (type) => type === 'trigger' ? { w: 60, h: 60 } : { w: 160, h: 50 }

  const handleNodeMouseDown = (e, node) => {
    if (e.button !== 0) return
    e.stopPropagation()
    const rect = canvasRef.current.getBoundingClientRect()
    setDraggingNode(node.id)
    setDragOffset({ x: (e.clientX - rect.left - pan.x) / zoom - node.position.x, y: (e.clientY - rect.top - pan.y) / zoom - node.position.y })
    
    if (e.ctrlKey || e.shiftKey) {
      setSelectedNodes(prev => {
        const next = new Set(prev)
        if (next.has(node.id)) next.delete(node.id)
        else next.add(node.id)
        return next
      })
    } else {
      if (!selectedNodes.has(node.id)) {
        setSelectedNodes(new Set([node.id]))
      }
    }
    setSelectedEdge(null)
  }

  const handleEdgeControlMouseDown = (e, edge) => {
    if (e.button !== 0) return
    e.stopPropagation()
    setDraggingEdge(edge.id)
    setSelectedEdge(edge.id)
    setSelectedNodes(new Set())
  }

  const handleMouseMove = useCallback((e) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    const mouseX = (e.clientX - rect.left - pan.x) / zoom
    const mouseY = (e.clientY - rect.top - pan.y) / zoom
    setMousePos({ x: mouseX, y: mouseY })

    if (draggingNode) {
      const dx = mouseX - dragOffset.x - (nodes.find(n => n.id === draggingNode)?.position.x || 0)
      const dy = mouseY - dragOffset.y - (nodes.find(n => n.id === draggingNode)?.position.y || 0)
      
      if (dx !== 0 || dy !== 0) {
         setNodes(prevNodes => prevNodes.map(n => {
           if (selectedNodes.has(n.id)) {
             return { ...n, position: snapPos({ x: n.position.x + dx, y: n.position.y + dy }) }
           }
           return n
         }))
      }
    } else if (selectionBox) {
      setSelectionBox(prev => ({ ...prev, current: { x: mouseX, y: mouseY } }))
    } else if (draggingEdge) {
      const edge = edges.find(ed => ed.id === draggingEdge)
      const src = nodes.find(n => n.id === edge?.source)
      const tgt = nodes.find(n => n.id === edge?.target)
      if (src && tgt) {
        const srcS = getNodeSize(src.type), tgtS = getNodeSize(tgt.type)
        const midX = (src.position.x + srcS.w + tgt.position.x) / 2
        const midY = (src.position.y + srcS.h/2 + tgt.position.y + tgtS.h/2) / 2
        const cp = { x: mouseX - midX, y: mouseY - midY }
        setEdges(edges.map(ed => ed.id === draggingEdge ? { ...ed, cp } : ed))
      }
    } else if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y })
    }
  }, [draggingNode, draggingEdge, selectionBox, isPanning, pan, zoom, dragOffset, nodes, edges, panStart, selectedNodes])

  const handleMouseUp = useCallback(() => { 
    if (draggingNode) saveHistory() 
    
    if (selectionBox) {
      const x1 = Math.min(selectionBox.start.x, selectionBox.current.x)
      const x2 = Math.max(selectionBox.start.x, selectionBox.current.x)
      const y1 = Math.min(selectionBox.start.y, selectionBox.current.y)
      const y2 = Math.max(selectionBox.start.y, selectionBox.current.y)
      
      const newSelection = new Set(selectedNodes)
      nodes.forEach(n => {
        const nx = n.position.x
        const ny = n.position.y
        const ns = getNodeSize(n.type)
        const nw = ns.w
        const nh = ns.h
        if (nx < x2 && nx + nw > x1 && ny < y2 && ny + nh > y1) {
          newSelection.add(n.id)
        }
      })
      setSelectedNodes(newSelection)
      setSelectionBox(null)
    }

    setDraggingNode(null); setDraggingEdge(null); setIsPanning(false) 
  }, [draggingNode, saveHistory, selectionBox, nodes, selectedNodes])

  const handleCanvasMouseDown = (e) => {
    if (e.target === canvasRef.current || e.target.tagName === 'svg') {
      if (e.button === 1 || (e.button === 0 && e.altKey)) {
        e.preventDefault(); setIsPanning(true); setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
      } else if (e.button === 0) { 
        if (!e.shiftKey && !e.ctrlKey) setSelectedNodes(new Set())
        const rect = canvasRef.current.getBoundingClientRect()
        const startX = (e.clientX - rect.left - pan.x) / zoom
        const startY = (e.clientY - rect.top - pan.y) / zoom
        setSelectionBox({ start: { x: startX, y: startY }, current: { x: startX, y: startY } })
        setSelectedEdge(null); setConnecting(null) 
      }
    }
  }

  const handleWheel = useCallback((e) => {
    e.preventDefault()
    if (e.ctrlKey || e.metaKey) {
      const delta = -e.deltaY * 0.002
      const newZoom = Math.min(2.5, Math.max(0.2, zoom + delta))
      const rect = canvasRef.current.getBoundingClientRect()
      const mx = e.clientX - rect.left, my = e.clientY - rect.top
      const sf = newZoom / zoom
      setPan({ x: mx - (mx - pan.x) * sf, y: my - (my - pan.y) * sf })
      setZoom(newZoom)
    } else {
      setPan({ x: pan.x - e.deltaX, y: pan.y - e.deltaY })
    }
  }, [zoom, pan])

  useEffect(() => {
    const c = canvasRef.current
    if (c) { c.addEventListener('wheel', handleWheel, { passive: false }); return () => c.removeEventListener('wheel', handleWheel) }
  }, [handleWheel])

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp) }
  }, [handleMouseMove, handleMouseUp])

  const deleteNode = (id) => { saveHistory(); setNodes(nodes.filter(n => n.id !== id)); setEdges(edges.filter(e => e.source !== id && e.target !== id)); setSelectedNodes(new Set()); setEditingNode(null) }
  const deleteEdge = (id) => { saveHistory(); setEdges(edges.filter(e => e.id !== id)); setSelectedEdge(null) }
  const duplicateNode = (node) => { saveHistory(); setNodes([...nodes, { ...node, id: `${node.type}-${Date.now()}`, position: snapPos({ x: node.position.x + 50, y: node.position.y + 50 }), data: { ...node.data } }]) }
  const startConnection = (id, e) => { e.stopPropagation(); setConnecting(id) }
  const endConnection = (id, e) => { 
    e.stopPropagation()
    if (connecting && connecting !== id && !edges.some(e => e.source === connecting && e.target === id)) { 
      saveHistory()
      const srcNode = nodes.find(n => n.id === connecting)
      // Auto-label et sourceHandle pour les nœuds condition : "yes" pour la 1ère sortie, "no" pour la 2ème
      let label = undefined
      let sourceHandle = undefined
      if (srcNode?.type === 'condition') {
        const existingOutEdges = edges.filter(e => e.source === connecting)
        if (existingOutEdges.length === 0) {
          label = 'Oui'
          sourceHandle = 'yes'
        } else {
          label = 'Non'
          sourceHandle = 'no'
        }
      }
      setEdges([...edges, { id: `e-${Date.now()}`, source: connecting, target: id, label, sourceHandle, cp: { x: 0, y: 0 } }])
    } 
    setConnecting(null) 
  }
  const fitToView = () => { if (nodes.length === 0) return; const rect = canvasRef.current.getBoundingClientRect(); const minX = Math.min(...nodes.map(n => n.position.x)); const maxX = Math.max(...nodes.map(n => n.position.x + 220)); const minY = Math.min(...nodes.map(n => n.position.y)); const maxY = Math.max(...nodes.map(n => n.position.y + 100)); const cw = maxX - minX + 100, ch = maxY - minY + 100; const nz = Math.min(rect.width / cw, rect.height / ch, 1.5) * 0.9; setZoom(nz); setPan({ x: (rect.width - cw * nz) / 2 - minX * nz + 50, y: (rect.height - ch * nz) / 2 - minY * nz + 50 }) }
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  //Algorithme de Bézier pour les liens
  const getEdgePath = (edge) => {
    const src = nodes.find(n => n.id === edge.source)
    const tgt = nodes.find(n => n.id === edge.target)
    if (!src || !tgt) return { path: '', lp: { x: 0, y: 0 }, cp: { x: 0, y: 0 } }
    
    const srcSize = getNodeSize(src.type)
    const tgtSize = getNodeSize(tgt.type)
    const srcW = srcSize.w
    const srcH = srcSize.h
    const tgtH = tgtSize.h
    
    const sx = src.position.x + srcW
    const sy = src.position.y + (srcH / 2)
    const tx = tgt.position.x
    const ty = tgt.position.y + (tgtH / 2)
    
    const dx = tx - sx
    const dy = ty - sy
    const dist = Math.sqrt(dx*dx + dy*dy)
    
    let cp1x, cp1y, cp2x, cp2y

    if (dx < 0) {
      // Loopback accentué
      const loopSize = Math.max(150, Math.abs(dy))
      cp1x = sx + loopSize * 0.8
      cp1y = sy + (dy > 0 ? 80 : -80)
      cp2x = tx - loopSize * 0.8
      cp2y = ty + (dy > 0 ? -80 : 80)
    } else {
      // Courbe standard "S Sexy"
      // On force une sortie horizontale longue (min 100px ou 60% de la distance)
      const curvature = Math.max(dist * 0.5, 120) 
      cp1x = sx + curvature
      cp1y = sy
      cp2x = tx - curvature
      cp2y = ty
    }

    const c = edge.cp || { x: 0, y: 0 }
    const mx = (sx + tx) / 2 + c.x
    const my = (sy + ty) / 2 + c.y
    
    if (c.x === 0 && c.y === 0) {
       return { 
         path: `M ${sx} ${sy} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${tx} ${ty}`, 
         lp: { x: (sx+tx)/2, y: (sy+ty)/2 - 15 },
         cp: { x: (sx+tx)/2, y: (sy+ty)/2 } 
       }
    }

    return { path: `M ${sx} ${sy} Q ${mx} ${my}, ${tx} ${ty}`, lp: { x: mx, y: my - 15 }, cp: { x: mx, y: my } }
  }

  const selNode = nodes.find(n => selectedNodes.has(n.id) && selectedNodes.size === 1)

  return (
    <div ref={containerRef} style={{ display: 'flex', height: isFullscreen ? '100vh' : 'calc(100vh - 200px)', minHeight: '600px', background: 'rgb(var(--rgb-canvas))', borderRadius: isFullscreen ? 0 : '12px', overflow: 'hidden', border: isFullscreen ? 'none' : '1px solid rgb(var(--rgb-ink) / 0.1)', position: 'relative' }}>
      {/* Left Panel */}
      <div style={{ width: '200px', background: 'rgb(var(--rgb-canvas))', borderRight: '1px solid rgb(var(--rgb-ink) / 0.1)', padding: '0.75rem', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        {/* Nom de la séquence */}
        <div style={{ marginBottom: '0.75rem' }}>
          <input
            type="text"
            value={sequenceName}
            onChange={(e) => setSequenceName(e.target.value)}
            placeholder="Nom de la séquence..."
            style={{
              width: '100%',
              padding: '0.5rem',
              background: 'rgb(var(--rgb-ink) / 0.05)',
              border: '1px solid rgb(var(--rgb-ink) / 0.1)',
              borderRadius: '6px',
              color: 'var(--color-text)',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button 
            onClick={() => setShowJsonImport(true)}
            style={{ flex: 1, background: 'rgb(var(--rgb-ink) / 0.1)', color: 'var(--color-text)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', padding: '0.4rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}
          >
            <FileText size={12} />JSON
          </button>
          <button 
            onClick={handleSaveWorkflow}
            disabled={isSaving}
            style={{ 
              flex: 1, 
              background: saveSuccess ? 'rgba(16,185,129,0.2)' : 'rgb(var(--rgb-ink) / 0.1)', 
              color: saveSuccess ? '#10b981' : 'var(--color-text)', 
              border: saveSuccess ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgb(var(--rgb-ink) / 0.1)', 
              padding: '0.4rem', 
              borderRadius: '6px', 
              fontWeight: 600, 
              fontSize: '0.75rem', 
              cursor: isSaving ? 'wait' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.3rem',
              transition: 'all 0.2s'
            }}
          >
            {isSaving ? <Loader2 size={12} className="tw:animate-spin" style={{ animation: 'spin 1s linear infinite' }} /> : 
             saveSuccess ? <Check size={12} /> : <Save size={12} />}
            {isSaving ? '...' : saveSuccess ? 'OK!' : (sequenceId ? 'Màj' : 'Créer')}
          </button>
        </div>
        {/* Debug button (pre-prod only) */}
        <button 
          onClick={loadDebugData}
          disabled={debugLoading || !sequenceId}
          style={{ 
            width: '100%',
            marginBottom: '0.75rem',
            background: 'rgba(239,68,68,0.1)', 
            color: '#ef4444', 
            border: '1px solid rgba(239,68,68,0.2)', 
            padding: '0.4rem', 
            borderRadius: '6px', 
            fontWeight: 600, 
            fontSize: '0.7rem', 
            cursor: !sequenceId ? 'not-allowed' : debugLoading ? 'wait' : 'pointer',
            opacity: !sequenceId ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.3rem'
          }}
        >
          {debugLoading ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Eye size={12} />}
          🔧 Debug Panel
        </button>
        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-disabled)', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Composants</span>
          <button 
            onClick={() => console.log('Current workflow:', JSON.stringify({ nodes, edges }, null, 2))}
            title="Afficher le JSON dans la console"
            style={{ background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: '0.2rem' }}
          >
            <Eye size={12} />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flex: 1 }}>
          {Object.values(NODE_TYPES).map((nt) => {
            const Icon = nt.icon
            return (
              <div key={nt.type} draggable onDragStart={(e) => handleDragStart(e, nt.type)} style={{ padding: '0.5rem', background: 'rgb(var(--rgb-ink) / 0.02)', border: '1px solid rgb(var(--rgb-ink) / 0.06)', borderRadius: '8px', cursor: 'grab', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.05)'; e.currentTarget.style.borderColor = nt.color + '40' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.02)'; e.currentTarget.style.borderColor = 'rgb(var(--rgb-ink) / 0.06)' }}>
                <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: nt.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={12} color={nt.color} /></div>
                <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{nt.label}</span>
              </div>
            )
          })}
        </div>
        <div style={{ borderTop: '1px solid rgb(var(--rgb-ink) / 0.1)', paddingTop: '0.6rem', marginTop: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.4rem' }}>
             <button onClick={performUndo} disabled={history.past.length === 0} title="Annuler (Ctrl+Z)" style={{ flex: 1, padding: '0.4rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.08)', borderRadius: '6px', color: history.past.length ? 'var(--color-text)' : 'rgb(var(--rgb-border-active))', cursor: history.past.length ? 'pointer' : 'not-allowed', display: 'flex', justifyContent: 'center' }}><Undo size={14} /></button>
             <button onClick={performRedo} disabled={history.future.length === 0} title="Rétablir (Ctrl+Y)" style={{ flex: 1, padding: '0.4rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.08)', borderRadius: '6px', color: history.future.length ? 'var(--color-text)' : 'rgb(var(--rgb-border-active))', cursor: history.future.length ? 'pointer' : 'not-allowed', display: 'flex', justifyContent: 'center' }}><Redo size={14} /></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.35rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px', border: '1px solid rgb(var(--rgb-ink) / 0.06)', marginBottom: '0.4rem' }}>
            <button onClick={() => setZoom(z => Math.max(0.2, z - 0.15))} title="Zoom -" style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', padding: '0.2rem' }}><ZoomOut size={14} /></button>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(2.5, z + 0.15))} title="Zoom +" style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', padding: '0.2rem' }}><ZoomIn size={14} /></button>
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button onClick={fitToView} title="Recentrer la vue" style={{ flex: 1, padding: '0.4rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.08)', borderRadius: '6px', color: 'var(--color-text-secondary)', cursor: 'pointer', display: 'flex', justifyContent: 'center' }}><Focus size={14} /></button>
            <button onClick={toggleFullscreen} title={isFullscreen ? 'Quitter plein écran' : 'Plein écran'} style={{ flex: 1, padding: '0.4rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.08)', borderRadius: '6px', color: 'var(--color-text-secondary)', cursor: 'pointer', display: 'flex', justifyContent: 'center' }}>{isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</button>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div ref={canvasRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', background: 'radial-gradient(circle at 1px 1px, rgb(var(--rgb-ink) / 0.08) 1px, transparent 0)', backgroundSize: `${GRID_SIZE * zoom}px ${GRID_SIZE * zoom}px`, backgroundPosition: `${pan.x}px ${pan.y}px`, cursor: isPanning ? 'grabbing' : (connecting ? 'crosshair' : 'default'), touchAction: 'none' }} onMouseDown={handleCanvasMouseDown} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
        <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
          <defs><marker id="arrowhead" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto"><polygon points="0 0, 6 2, 0 4" fill="rgba(91, 92, 244, 0.7)" /></marker></defs>
          <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
            {edges.map((edge) => { const { path, lp, cp } = getEdgePath(edge); const isSel = selectedEdge === edge.id; return (
              <g key={edge.id}>
                <path d={path} fill="none" stroke="transparent" strokeWidth={20} style={{ pointerEvents: 'stroke', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setSelectedEdge(edge.id); setSelectedNodes(new Set()) }} />
                <path d={path} fill="none" stroke={isSel ? '#5b5cf4' : 'rgba(91, 92, 244, 0.4)'} strokeWidth={isSel ? 3 : 2} markerEnd="url(#arrowhead)" />
                                {isSel && <circle cx={cp.x} cy={cp.y} r={8} fill="#5b5cf4" stroke="#0a0a0a" strokeWidth={2} style={{ cursor: 'move', pointerEvents: 'all' }} onMouseDown={(e) => handleEdgeControlMouseDown(e, edge)} />}
                {edge.label && <><rect x={lp.x - 20} y={lp.y - 10} width={40} height={20} rx={10} fill="rgb(var(--rgb-surface-hover))" stroke={isSel ? '#5b5cf4' : 'rgb(var(--rgb-ink) / 0.2)'} /><text x={lp.x} y={lp.y + 4} fill={isSel ? '#5b5cf4' : 'var(--color-text-secondary)'} fontSize="11" textAnchor="middle" fontWeight="500">{edge.label}</text></>}
              </g>
            )})}
            {connecting && (() => { const cn = nodes.find(n => n.id === connecting); const cs = cn ? getNodeSize(cn.type) : { w: 220, h: 100 }; return <line x1={(cn?.position.x || 0) + cs.w} y1={(cn?.position.y || 0) + cs.h / 2} x2={mousePos.x} y2={mousePos.y} stroke="#5b5cf4" strokeWidth={2} strokeDasharray="8 4" opacity={0.8} /> })()}
            {/* Selection Box */}
            {selectionBox && <rect x={Math.min(selectionBox.start.x, selectionBox.current.x)} y={Math.min(selectionBox.start.y, selectionBox.current.y)} width={Math.abs(selectionBox.current.x - selectionBox.start.x)} height={Math.abs(selectionBox.current.y - selectionBox.start.y)} fill="rgba(91, 92, 244, 0.1)" stroke="#5b5cf4" strokeWidth={1} strokeDasharray="4 2" />}
          </g>
        </svg>
        <div style={{ position: 'absolute', top: 0, left: 0, transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
          {nodes.map((node) => <WorkflowNode key={node.id} node={node} nodeType={NODE_TYPES[node.type]} isSelected={selectedNodes.has(node.id)} onMouseDown={(e) => handleNodeMouseDown(e, node)} onClick={() => { setSelectedNodes(new Set([node.id])); setSelectedEdge(null) }} onDoubleClick={() => setEditingNode(node)} onStartConnect={(e) => startConnection(node.id, e)} onEndConnect={(e) => endConnection(node.id, e)} onDelete={() => deleteNode(node.id)} onDuplicate={() => duplicateNode(node)} connecting={connecting} onHover={(state) => setHoveredNode(state ? node : null)} />)}
        </div>
        {/* Context Tooltip */}
        {hoveredNode && !draggingNode && (
           <div style={{ position: 'absolute', left: (hoveredNode.position.x * zoom) + pan.x, top: (hoveredNode.position.y * zoom) + pan.y - 40, background: 'rgb(var(--rgb-surface-active))', border: '1px solid rgb(var(--rgb-border-active))', padding: '0.5rem 0.8rem', borderRadius: '6px', zIndex: 100, pointerEvents: 'none', boxShadow: '0 4px 12px rgb(var(--rgb-shadow) / calc(0.5 * var(--shadow-k)))', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: NODE_TYPES[hoveredNode.type]?.color || 'var(--color-text-secondary)' }}></div>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text)' }}>{hoveredNode.data.label}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--color-text-secondary)' }}>ID: {hoveredNode.id.split('-')[1]}</div>
              </div>
           </div>
        )}
        {nodes.length === 0 && <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: 'var(--color-text-disabled)' }}><MousePointer2 size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} /><p style={{ fontSize: '1rem' }}>Glissez des composants ici</p><p style={{ fontSize: '0.85rem', color: 'rgb(var(--rgb-border-active))' }}>Scroll 2 doigts = Pan • Ctrl+Scroll = Zoom</p></div>}
        {selectedEdge && <div style={{ position: 'absolute', bottom: '1rem', left: '50%', transform: 'translateX(-50%)', background: 'rgb(var(--rgb-surface-hover))', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '8px', padding: '0.5rem 1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}><span style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>Glissez le point pour courber</span><button onClick={() => deleteEdge(selectedEdge)} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '0.35rem 0.75rem', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}><Trash2 size={14} />Supprimer</button></div>}
        <div style={{ position: 'absolute', bottom: '0.75rem', right: '0.75rem', fontSize: '0.7rem', color: 'rgb(var(--rgb-border-active))', pointerEvents: 'none' }}>Scroll: Pan • Ctrl+Scroll: Zoom • Double-clic: Éditer</div>
      </div>

      {/* JSON Import Modal */}
      {showJsonImport && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgb(var(--rgb-shade) / 0.7)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 1000 
        }}>
          <div style={{ 
            background: 'rgb(var(--rgb-surface-hover))', 
            border: '1px solid rgb(var(--rgb-ink) / 0.1)', 
            borderRadius: '12px', 
            width: '90%', 
            maxWidth: '800px', 
            maxHeight: '80%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{ 
              padding: '1.5rem', 
              borderBottom: '1px solid rgb(var(--rgb-ink) / 0.1)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '8px', 
                  background: 'rgba(91, 92, 244,0.1)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <FileText size={16} color="#5b5cf4" />
                </div>
                <div>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-text)', margin: 0 }}>Import JSON</h2>
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', margin: 0 }}>Collez votre workflow JSON pour le tester rapidement</p>
                </div>
              </div>
              <button 
                onClick={() => setShowJsonImport(false)}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: 'var(--color-text-secondary)', 
                  cursor: 'pointer', 
                  padding: '0.5rem',
                  borderRadius: '6px' 
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Content */}
            <div style={{ padding: '1.5rem', flex: 1, overflow: 'auto' }}>
              <div style={{ display: 'flex', gap: '1rem', height: '100%', minHeight: '400px' }}>
                {/* Textarea */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-text)', marginBottom: '0.5rem' }}>
                    Workflow JSON:
                  </label>
                  <textarea
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    placeholder="Collez votre JSON ici..."
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: 'rgb(var(--rgb-ink) / 0.05)',
                      border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                      borderRadius: '8px',
                      color: 'var(--color-text)',
                      fontSize: '0.85rem',
                      fontFamily: 'Monaco, "Cascadia Code", "Roboto Mono", monospace',
                      resize: 'none',
                      outline: 'none'
                    }}
                  />
                </div>
                
                {/* Example */}
                <div style={{ width: '350px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--color-text)' }}>
                      Exemple:
                    </label>
                    <button
                      onClick={() => setJsonInput(getExampleJson())}
                      style={{
                        background: 'rgba(91, 92, 244,0.1)',
                        border: '1px solid rgba(91, 92, 244,0.3)',
                        borderRadius: '6px',
                        padding: '0.35rem 0.75rem',
                        color: '#5b5cf4',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem'
                      }}
                    >
                      <Upload size={12} />
                      Utiliser
                    </button>
                  </div>
                  <div style={{
                    flex: 1,
                    padding: '1rem',
                    background: 'rgb(var(--rgb-ink) / 0.02)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.06)',
                    borderRadius: '8px',
                    overflow: 'auto',
                    fontSize: '0.7rem',
                    fontFamily: 'Monaco, "Cascadia Code", "Roboto Mono", monospace',
                    color: 'var(--color-text-secondary)',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {getExampleJson()}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div style={{ 
              padding: '1.5rem', 
              borderTop: '1px solid rgb(var(--rgb-ink) / 0.1)', 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center' 
            }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                Le JSON doit contenir les propriétés "nodes" et "edges"
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => setShowJsonImport(false)}
                  style={{
                    background: 'rgb(var(--rgb-ink) / 0.1)',
                    border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleJsonImport}
                  disabled={!jsonInput.trim()}
                  style={{
                    background: jsonInput.trim() ? 'rgba(91, 92, 244,0.1)' : 'rgb(var(--rgb-ink) / 0.05)',
                    border: jsonInput.trim() ? '1px solid rgba(91, 92, 244,0.3)' : '1px solid rgb(var(--rgb-ink) / 0.1)',
                    borderRadius: '8px',
                    padding: '0.75rem 1.5rem',
                    color: jsonInput.trim() ? '#5b5cf4' : 'var(--color-text-muted)',
                    cursor: jsonInput.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Upload size={16} />
                  Importer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Panel Modal (pre-prod only) */}
      {showDebugPanel && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgb(var(--rgb-shade) / 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '85vh',
            background: 'rgb(var(--rgb-canvas))',
            borderRadius: '12px',
            border: '1px solid rgba(239,68,68,0.3)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{ 
              padding: '1rem', 
              borderBottom: '1px solid rgb(var(--rgb-ink) / 0.1)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              background: 'rgba(239,68,68,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.5rem' }}>🔧</span>
                <div>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#ef4444', margin: 0 }}>Debug Panel</h2>
                  <p style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', margin: 0 }}>Diagnostic séquence - {debugData?.name || sequenceName}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => loadDebugData({ page: debugFilters.page })} disabled={debugLoading} style={{ padding: '0.5rem 1rem', background: 'rgb(var(--rgb-ink) / 0.05)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'rgb(var(--rgb-ink) / 0.75)', cursor: 'pointer', fontSize: '0.8rem' }}>
                  ↻ Refresh
                </button>
                <button onClick={handleRunAll} disabled={debugLoading} style={{ padding: '0.5rem 1rem', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', color: '#10b981', cursor: 'pointer', fontSize: '0.8rem' }}>
                  <Play size={14} style={{ marginRight: '0.3rem' }} />Run All
                </button>
                <button onClick={() => { setShowDebugPanel(false); setSelectedEnrollment(null) }} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', padding: '0.5rem' }}>
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: '1rem', display: 'flex', gap: '1rem' }}>
              {/* Left: Enrollments list */}
              <div style={{ width: '320px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text)', margin: 0 }}>
                    Enrollments ({enrollmentsTotal})
                  </h3>
                  <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                    Page {debugFilters.page}
                  </span>
                </div>

                {debugStats && (
                  <div style={{ padding: '0.6rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '8px', border: '1px solid rgb(var(--rgb-ink) / 0.08)' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>Statuts</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
                      {Object.entries(debugStats.byStatus || {}).map(([status, count]) => (
                        <div key={status} style={{ padding: '0.35rem', background: 'rgb(var(--rgb-ink) / 0.03)', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>{status}</div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--color-text)', fontWeight: 600 }}>{count}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>
                      Prêts à exécuter: <span style={{ color: '#10b981', fontWeight: 600 }}>{debugStats.readyToProcess ?? 0}</span>
                    </div>
                  </div>
                )}

                <div style={{ padding: '0.6rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '8px', border: '1px solid rgb(var(--rgb-ink) / 0.08)' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', marginBottom: '0.4rem' }}>Inscrire un prospect</div>
                  <input
                    type="text"
                    value={enrollProspectId}
                    onChange={(e) => setEnrollProspectId(e.target.value)}
                    placeholder="Prospect ID"
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.6rem',
                      background: 'rgb(var(--rgb-ink) / 0.04)',
                      border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                      borderRadius: '6px',
                      color: 'var(--color-text)',
                      fontSize: '0.75rem',
                      marginBottom: '0.5rem'
                    }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.7rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={enrollForceReset}
                      onChange={(e) => setEnrollForceReset(e.target.checked)}
                      style={{ accentColor: '#5b5cf4' }}
                    />
                    Réinitialiser si déjà inscrit
                  </label>
                  <button onClick={handleEnrollProspect} disabled={debugLoading || !enrollProspectId.trim()} style={{ width: '100%', padding: '0.45rem', background: 'rgba(91, 92, 244,0.1)', border: '1px solid rgba(91, 92, 244,0.3)', borderRadius: '6px', color: '#5b5cf4', cursor: 'pointer', fontSize: '0.75rem' }}>
                    Inscrire
                  </button>
                </div>

                <div style={{ padding: '0.6rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '8px', border: '1px solid rgb(var(--rgb-ink) / 0.08)' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', marginBottom: '0.4rem' }}>Filtres</div>
                  <input
                    type="text"
                    value={debugFilters.q}
                    onChange={(e) => setDebugFilters(curr => ({ ...curr, q: e.target.value }))}
                    placeholder="Rechercher..."
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.6rem',
                      background: 'rgb(var(--rgb-ink) / 0.04)',
                      border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                      borderRadius: '6px',
                      color: 'var(--color-text)',
                      fontSize: '0.75rem',
                      marginBottom: '0.5rem'
                    }}
                  />
                  <select
                    value={debugFilters.status}
                    onChange={(e) => setDebugFilters(curr => ({ ...curr, status: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.6rem',
                      background: 'rgb(var(--rgb-ink) / 0.04)',
                      border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                      borderRadius: '6px',
                      color: 'var(--color-text)',
                      fontSize: '0.75rem',
                      marginBottom: '0.5rem'
                    }}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                    <option value="exited">Exited</option>
                  </select>
                  <select
                    value={debugFilters.limit}
                    onChange={(e) => setDebugFilters(curr => ({ ...curr, limit: Number(e.target.value) }))}
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.6rem',
                      background: 'rgb(var(--rgb-ink) / 0.04)',
                      border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                      borderRadius: '6px',
                      color: 'var(--color-text)',
                      fontSize: '0.75rem',
                      marginBottom: '0.5rem'
                    }}
                  >
                    {[25, 50, 100, 200].map((size) => (
                      <option key={size} value={size}>{size} / page</option>
                    ))}
                  </select>
                  <button onClick={() => loadDebugData({ page: 1 })} disabled={debugLoading} style={{ width: '100%', padding: '0.45rem', background: 'rgb(var(--rgb-ink) / 0.08)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'rgb(var(--rgb-ink) / 0.88)', cursor: 'pointer', fontSize: '0.75rem' }}>
                    Appliquer
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => loadDebugData({ page: Math.max(1, debugFilters.page - 1) })} disabled={debugLoading || debugFilters.page <= 1} style={{ flex: 1, padding: '0.45rem', background: 'rgb(var(--rgb-ink) / 0.05)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>
                    ◀ Prev
                  </button>
                  <button onClick={() => loadDebugData({ page: debugFilters.page + 1 })} disabled={debugLoading || !enrollmentsHasMore} style={{ flex: 1, padding: '0.45rem', background: 'rgb(var(--rgb-ink) / 0.05)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>
                    Next ▶
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '320px', overflow: 'auto' }}>
                  {enrollments.length === 0 ? (
                    <div style={{ padding: '1rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                      Aucun enrollment
                    </div>
                  ) : enrollments.map(e => (
                    <button
                      key={e.id}
                      onClick={() => loadEnrollmentDebug(e.id)}
                      style={{
                        padding: '0.6rem',
                        background: selectedEnrollment?.enrollment_id === e.id ? 'rgba(239,68,68,0.1)' : 'rgb(var(--rgb-ink) / 0.02)',
                        border: selectedEnrollment?.enrollment_id === e.id ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgb(var(--rgb-ink) / 0.05)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text)', fontWeight: 500 }}>
                        {e.recipient?.fullName || e.recipient?.firstName || e.prospect?.firstName} {!e.recipient?.fullName && (e.recipient?.lastName || e.prospect?.lastName)}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>
                        {e.recipient?.email || e.prospect?.primaryEmail || 'No email'}
                      </div>
                      <div style={{ fontSize: '0.65rem', color: e.status === 'active' ? '#10b981' : e.status === 'completed' ? '#3b82f6' : '#f59e0b', marginTop: '0.25rem' }}>
                        {e.status} • Step {e.currentStep}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Right: Selected enrollment details */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {!selectedEnrollment ? (
                  <div style={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: 'rgb(var(--rgb-ink) / 0.02)',
                    borderRadius: '8px',
                    color: 'var(--color-text-muted)',
                    fontSize: '0.85rem'
                  }}>
                    Sélectionnez un enrollment pour voir les détails
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button onClick={() => handleExecuteStep(selectedEnrollment.enrollment_id)} disabled={debugLoading} style={{ padding: '0.5rem 0.75rem', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', color: '#10b981', cursor: 'pointer', fontSize: '0.75rem' }}>
                        ▶ Exécuter Step
                      </button>
                      <button onClick={() => handleResetEnrollment(selectedEnrollment.enrollment_id)} disabled={debugLoading} style={{ padding: '0.5rem 0.75rem', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '6px', color: '#3b82f6', cursor: 'pointer', fontSize: '0.75rem' }}>
                        ⟲ Reset Step0
                      </button>
                      <button onClick={() => handleResetTracking(selectedEnrollment.enrollment_id)} disabled={debugLoading} style={{ padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>
                        🔄 Reset Tracking
                      </button>
                      <button onClick={() => handleRemoveEnrollment(selectedEnrollment.enrollment_id)} disabled={debugLoading} style={{ padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>
                        🗑 Supprimer
                      </button>
                      <button onClick={() => loadEnrollmentDebug(selectedEnrollment.enrollment_id)} disabled={debugLoading} style={{ padding: '0.5rem 0.75rem', background: 'rgb(var(--rgb-ink) / 0.05)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>
                        ↻ Refresh
                      </button>
                    </div>
                    
                    {/* Stats Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                      {[
                        { label: 'Emails Sent', value: selectedEnrollment.emails_sent, color: '#3b82f6' },
                        { label: 'Emails Opened', value: selectedEnrollment.emails_opened, color: selectedEnrollment.emails_opened > 0 ? '#10b981' : 'var(--color-text-muted)' },
                        { label: 'Emails Clicked', value: selectedEnrollment.emails_clicked, color: selectedEnrollment.emails_clicked > 0 ? '#f59e0b' : 'var(--color-text-muted)' },
                        { label: 'Current Step', value: selectedEnrollment.current_step, color: '#8b5cf6' }
                      ].map((stat, i) => (
                        <div key={i} style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--color-text-secondary)' }}>{stat.label}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>Mise à jour admin (tests)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <select
                          value={adminStatus}
                          onChange={(e) => setAdminStatus(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.45rem 0.6rem',
                            background: 'rgb(var(--rgb-ink) / 0.04)',
                            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                            borderRadius: '6px',
                            color: 'var(--color-text)',
                            fontSize: '0.75rem'
                          }}
                        >
                          <option value="">Status</option>
                          <option value="pending">Pending</option>
                          <option value="active">Active</option>
                          <option value="paused">Paused</option>
                          <option value="completed">Completed</option>
                          <option value="exited">Exited</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          value={adminCurrentStep}
                          onChange={(e) => setAdminCurrentStep(e.target.value)}
                          placeholder="Current step"
                          style={{
                            width: '100%',
                            padding: '0.45rem 0.6rem',
                            background: 'rgb(var(--rgb-ink) / 0.04)',
                            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                            borderRadius: '6px',
                            color: 'var(--color-text)',
                            fontSize: '0.75rem'
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem', fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <input type="checkbox" checked={adminResetCounters} onChange={(e) => setAdminResetCounters(e.target.checked)} style={{ accentColor: '#5b5cf4' }} />
                          Reset counters
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <input type="checkbox" checked={adminResetHistory} onChange={(e) => setAdminResetHistory(e.target.checked)} style={{ accentColor: '#5b5cf4' }} />
                          Reset history
                        </label>
                      </div>
                      <button onClick={handleAdminUpdate} disabled={debugLoading} style={{ padding: '0.45rem 0.75rem', background: 'rgb(var(--rgb-ink) / 0.08)', border: '1px solid rgb(var(--rgb-ink) / 0.12)', borderRadius: '6px', color: 'rgb(var(--rgb-ink) / 0.88)', cursor: 'pointer', fontSize: '0.75rem' }}>
                        Appliquer
                      </button>
                    </div>
                    
                    {/* Condition Analysis */}
                    {selectedEnrollment.analysis && (
                      <div style={{ padding: '0.75rem', background: selectedEnrollment.analysis.condition_would_pass ? 'rgba(16,185,129,0.05)' : 'rgba(239,68,68,0.05)', border: `1px solid ${selectedEnrollment.analysis.condition_would_pass ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`, borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: selectedEnrollment.analysis.condition_would_pass ? '#10b981' : '#ef4444', marginBottom: '0.25rem' }}>
                          📊 Condition "email_opened": {selectedEnrollment.analysis.condition_would_pass ? 'TRUE (Oui)' : 'FALSE (Non)'}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>
                          {selectedEnrollment.analysis.condition_explanation}
                        </div>
                      </div>
                    )}
                    
                    {/* Current Node */}
                    <div style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>Current Node ID</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text)', fontFamily: 'monospace' }}>{selectedEnrollment.current_node_id || 'null'}</div>
                    </div>
                    
                    {/* Node History */}
                    <div style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>Node History</div>
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        {(selectedEnrollment.node_history || []).map((nodeId, i) => (
                          <span key={i} style={{ padding: '0.25rem 0.5rem', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '4px', fontSize: '0.7rem', color: '#8b5cf6', fontFamily: 'monospace' }}>
                            {nodeId}
                          </span>
                        ))}
                        {(selectedEnrollment.node_history || []).length === 0 && <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>Vide</span>}
                      </div>
                    </div>
                    
                    {/* Edges Analysis */}
                    {selectedEnrollment.condition_edges_analysis && Object.keys(selectedEnrollment.condition_edges_analysis).length > 0 && (
                      <div style={{ padding: '0.75rem', background: 'rgb(var(--rgb-ink) / 0.02)', borderRadius: '6px' }}>
                        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>Condition Edges Analysis</div>
                        {Object.entries(selectedEnrollment.condition_edges_analysis).map(([nodeId, analysis]) => (
                          <div key={nodeId} style={{ marginBottom: '0.5rem' }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text)' }}>{analysis.label} ({nodeId})</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--color-text-secondary)' }}>Type: {analysis.condition_type}</div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                              {analysis.edges?.map((edge, i) => (
                                <span key={i} style={{ 
                                  padding: '0.2rem 0.5rem', 
                                  background: edge.is_yes_branch ? 'rgba(16,185,129,0.1)' : edge.is_no_branch ? 'rgba(239,68,68,0.1)' : 'rgb(var(--rgb-ink) / 0.05)',
                                  border: `1px solid ${edge.is_yes_branch ? 'rgba(16,185,129,0.3)' : edge.is_no_branch ? 'rgba(239,68,68,0.3)' : 'rgb(var(--rgb-ink) / 0.1)'}`,
                                  borderRadius: '4px',
                                  fontSize: '0.65rem',
                                  color: edge.is_yes_branch ? '#10b981' : edge.is_no_branch ? '#ef4444' : 'var(--color-text-secondary)'
                                }}>
                                  → {edge.target} {edge.label && `[${edge.label}]`} {edge.sourceHandle && `(${edge.sourceHandle})`}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Node Editor Panel - Full Screen (n8n style) */}
      {editingNode && <NodeEditorPanel 
        node={editingNode} 
        nodeType={NODE_TYPES[editingNode.type]} 
        onClose={() => setEditingNode(null)} 
        onSave={(data, nodeId) => { 
          const safeData = { ...data }; 
          // Assurer que le label n'est jamais vide
          if (!safeData.label || safeData.label.trim() === '') {
            safeData.label = NODE_TYPES[editingNode.type]?.label || 'Node';
          }
          if (safeData.delayValue === undefined && editingNode.type === 'delay') safeData.delayValue = 1; 
          setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...safeData } } : n)); 
          setEditingNode(null);
        }} 
        onDelete={() => { deleteNode(editingNode.id); setEditingNode(null) }} 
      />}
    </div>
  )
}

function WorkflowNode({ node, nodeType, isSelected, onMouseDown, onClick, onDoubleClick, onStartConnect, onEndConnect, onDelete, onDuplicate, connecting, onHover }) {
  const Icon = nodeType?.icon || Mail
  const isTrigger = node.type === 'trigger'
  const size = isTrigger ? { w: 60, h: 60 } : { w: 160, h: 50 }

  return (
    <div
      onMouseEnter={() => onHover && onHover(true)}
      onMouseLeave={() => onHover && onHover(false)}
      style={{ 
        position: 'absolute', 
        left: node.position.x, 
        top: node.position.y, 
        width: size.w, 
        height: size.h,
        background: 'rgb(var(--rgb-surface-hover))', 
        border: `2px solid ${isSelected ? nodeType?.color : 'rgb(var(--rgb-ink) / 0.12)'}`, 
        borderRadius: isTrigger ? '50%' : '8px', 
        cursor: 'grab', 
        userSelect: 'none', 
        boxShadow: isSelected ? `0 0 16px ${nodeType?.color}30` : '0 2px 8px rgb(var(--rgb-shade) / 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isTrigger ? 'center' : 'flex-start',
        gap: '0.5rem',
        padding: isTrigger ? 0 : '0 0.6rem',
        transition: 'border-color 0.15s, box-shadow 0.15s'
      }} 
      onMouseDown={onMouseDown} 
      onClick={(e) => { e.stopPropagation(); onClick() }} 
      onDoubleClick={onDoubleClick}
    >
      {/* Icon */}
      <div style={{ 
        width: isTrigger ? '28px' : '28px', 
        height: isTrigger ? '28px' : '28px', 
        borderRadius: isTrigger ? '50%' : '6px', 
        background: (nodeType?.color || '#5b5cf4') + '25', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon size={isTrigger ? 14 : 14} color={nodeType?.color} />
      </div>
      
      {/* Label (pas sur trigger) */}
      {!isTrigger && (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{ fontWeight: 600, fontSize: '0.75rem', color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.data.label}</div>
        </div>
      )}
      
      {/* Handles */}
      {!isTrigger && <div onClick={connecting ? onEndConnect : undefined} style={{ position: 'absolute', left: '-7px', top: '50%', transform: 'translateY(-50%)', width: '12px', height: '12px', borderRadius: '50%', background: connecting ? '#5b5cf4' : 'rgb(var(--rgb-surface-hover))', border: '2px solid #5b5cf4', cursor: connecting ? 'pointer' : 'default', zIndex: 10 }} />}
      <div onClick={onStartConnect} onMouseEnter={(e) => { if (!connecting) { e.currentTarget.style.background = '#5b5cf4'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.2)' } }} onMouseLeave={(e) => { if (!connecting) { e.currentTarget.style.background = 'rgb(var(--rgb-surface-hover))'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)' } }} style={{ position: 'absolute', right: '-7px', top: '50%', transform: 'translateY(-50%)', width: '12px', height: '12px', borderRadius: '50%', background: 'rgb(var(--rgb-surface-hover))', border: '2px solid #5b5cf4', cursor: 'crosshair', transition: 'all 0.15s', zIndex: 10 }} />
    </div>
  )
}

function NodeEditorPanel({ node, nodeType, onClose, onSave, onDelete }) {
  const [formData, setFormData] = useState({ ...node.data })
  const [showVars, setShowVars] = useState(false)
  const [tab, setTab] = useState('content')
  const bodyRef = useRef(null)
  const Icon = nodeType?.icon || Mail

  const insertVar = (k) => {
    const v = `{{${k}}}`
    if (bodyRef.current) {
      const s = bodyRef.current.selectionStart, e = bodyRef.current.selectionEnd, t = formData.body || ''
      setFormData({ ...formData, body: t.substring(0, s) + v + t.substring(e) })
      setTimeout(() => { bodyRef.current.selectionStart = bodyRef.current.selectionEnd = s + v.length; bodyRef.current.focus() }, 0)
    } else { setFormData({ ...formData, body: (formData.body || '') + v }) }
    setShowVars(false)
  }

  const insertTrackedLink = () => {
    const url = window.prompt('URL du lien à suivre :')
    if (!url) return
    const label = window.prompt('Texte du lien (optionnel) :', url) || url
    const value = `<a href="${url}">${label}</a>`
    if (bodyRef.current) {
      const s = bodyRef.current.selectionStart
      const e = bodyRef.current.selectionEnd
      const t = formData.body || ''
      setFormData({ ...formData, body: t.substring(0, s) + value + t.substring(e) })
      setTimeout(() => {
        bodyRef.current.selectionStart = bodyRef.current.selectionEnd = s + value.length
        bodyRef.current.focus()
      }, 0)
    } else {
      setFormData({ ...formData, body: (formData.body || '') + value })
    }
  }

  const insertUnsubscribeLink = () => {
    const label = window.prompt('Texte du lien de désinscription :', 'Se désinscrire') || 'Se désinscrire'
    const value = `<a href="{{lienDesinscription}}">${label}</a>`
    if (bodyRef.current) {
      const s = bodyRef.current.selectionStart
      const e = bodyRef.current.selectionEnd
      const t = formData.body || ''
      setFormData({ ...formData, body: t.substring(0, s) + value + t.substring(e) })
      setTimeout(() => {
        bodyRef.current.selectionStart = bodyRef.current.selectionEnd = s + value.length
        bodyRef.current.focus()
      }, 0)
    } else {
      setFormData({ ...formData, body: (formData.body || '') + value })
    }
  }

  return (
    <div style={{ 
      position: 'absolute', 
      inset: 0,
      background: 'rgb(var(--rgb-panel))', 
      display: 'flex', 
      flexDirection: 'column',
      zIndex: 200
    }}>
      {/* Top Bar - n8n style */}
      <div style={{ height: '50px', background: 'rgb(var(--rgb-surface-hover))', borderBottom: '1px solid rgb(var(--rgb-ink) / 0.1)', display: 'flex', alignItems: 'center', padding: '0 1rem', gap: '1rem' }}>
        <button onClick={() => onSave(formData, node.id)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem', transition: 'all 0.15s' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.05)'; e.currentTarget.style.color = 'var(--color-text)' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--color-text-secondary)' }}>
          <ArrowLeft size={16} />
          <span>Retour au canvas</span>
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.8rem', background: 'rgb(var(--rgb-ink) / 0.03)', borderRadius: '8px', border: '1px solid rgb(var(--rgb-ink) / 0.08)' }}>
          <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: (nodeType?.color) + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={12} color={nodeType?.color} />
          </div>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text)' }}>{formData.label}</span>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={onDelete} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem' }}><Trash2 size={14} />Supprimer</button>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', justifyContent: 'center' }}>
        {/* Center Panel - Parameters */}
        <div style={{ width: '100%', maxWidth: '700px', display: 'flex', flexDirection: 'column', background: 'rgb(var(--rgb-panel))' }}>
          {/* Node Header + Tabs */}
          <div style={{ padding: '1rem', borderBottom: '1px solid rgb(var(--rgb-ink) / 0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: node.type === 'email' ? '1rem' : 0 }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: (nodeType?.color) + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={18} color={nodeType?.color} />
              </div>
              <div style={{ flex: 1 }}>
                <input type="text" value={formData.label || ''} onChange={(e) => setFormData({ ...formData, label: e.target.value })} style={{ background: 'transparent', border: 'none', fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)', width: '100%', padding: 0 }} />
                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-disabled)', marginTop: '0.15rem' }}>{nodeType?.label}</div>
              </div>
            </div>
            
            {/* Tabs for email */}
            {node.type === 'email' && <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[{ id: 'content', label: 'Contenu' }, { id: 'preview', label: 'Aperçu' }].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderBottom: tab === t.id ? '2px solid #ff6d5a' : '2px solid transparent', color: tab === t.id ? 'rgb(var(--rgb-ink))' : 'var(--color-text-muted)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: tab === t.id ? 600 : 400 }}>{t.label}</button>
              ))}
            </div>}
          </div>

          {/* Content Area */}
          <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
            {node.type === 'email' && tab === 'content' && <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>Sujet</label>
                <input type="text" value={formData.subject || ''} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="Sujet de l'email..." style={{ width: '100%', padding: '0.7rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '0.9rem' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Corps du message</label>
                  <div style={{ position: 'relative', display: 'flex', gap: '0.4rem' }}>
                    <button onClick={insertTrackedLink} style={{ background: 'rgba(91, 92, 244,0.1)', border: '1px solid rgba(91, 92, 244,0.3)', borderRadius: '6px', padding: '0.3rem 0.6rem', color: '#5b5cf4', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><Link size={12} />Lien suivi</button>
                    <button onClick={insertUnsubscribeLink} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '0.3rem 0.6rem', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><UserMinus size={12} />Désinscription</button>
                    <button onClick={() => setShowVars(!showVars)} style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '6px', padding: '0.3rem 0.6rem', color: '#8b5cf6', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><Variable size={12} />Variables</button>
                    {showVars && <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '0.3rem', background: 'rgb(var(--rgb-surface-hover))', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', padding: '0.3rem', zIndex: 10, minWidth: '160px', boxShadow: '0 4px 16px rgb(var(--rgb-shadow) / calc(0.5 * var(--shadow-k)))' }}>
                      {MERGE_VARIABLES.map(v => <button key={v.key} onClick={() => insertVar(v.key)} style={{ width: '100%', padding: '0.4rem 0.6rem', background: 'transparent', border: 'none', color: 'rgb(var(--rgb-ink) / 0.75)', cursor: 'pointer', textAlign: 'left', borderRadius: '4px', fontSize: '0.75rem' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.05)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}><code style={{ color: '#8b5cf6' }}>{`{{${v.key}}}`}</code></button>)}
                    </div>}
                  </div>
                </div>
                <textarea ref={bodyRef} value={formData.body || ''} onChange={(e) => setFormData({ ...formData, body: e.target.value })} placeholder="Bonjour {{firstName}},&#10;&#10;..." rows={10} style={{ width: '100%', padding: '0.7rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '0.85rem', resize: 'vertical', fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: 1.5 }} />
              </div>
            </>}

            {node.type === 'email' && tab === 'preview' && <div style={{ background: '#fff', borderRadius: '8px', padding: '1.2rem', color: 'rgb(var(--rgb-border-hover))', fontFamily: 'Arial, Helvetica, sans-serif' }}>
              <div style={{ borderBottom: '1px solid rgb(var(--rgb-ink) / 0.88)', paddingBottom: '0.6rem', marginBottom: '0.8rem' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)', marginBottom: '0.15rem' }}>Sujet:</div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{formData.subject || '(Pas de sujet)'}</div>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.9rem' }}>{(formData.body || '(Pas de contenu)').replace(/\{\{(\w+)\}\}/g, (_, k) => `[${k}]`)}</div>
            </div>}

            {node.type === 'delay' && <div style={{ display: 'flex', gap: '0.8rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>Durée</label>
                <input type="number" min="1" value={formData.delayValue ?? 1} onChange={(e) => setFormData({ ...formData, delayValue: parseInt(e.target.value) || 1 })} style={{ width: '100%', padding: '0.7rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '0.9rem' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>Unité</label>
                <select value={formData.delayUnit || 'days'} onChange={(e) => setFormData({ ...formData, delayUnit: e.target.value })} style={{ width: '100%', padding: '0.7rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '0.9rem' }}><option value="minutes">Minutes</option><option value="hours">Heures</option><option value="days">Jours</option></select>
              </div>
            </div>}

            {node.type === 'condition' && <div>
              <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>Type de condition</label>
              <select value={formData.conditionType || 'email_opened'} onChange={(e) => setFormData({ ...formData, conditionType: e.target.value })} style={{ width: '100%', padding: '0.7rem', background: 'rgb(var(--rgb-ink) / 0.03)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: '6px', color: 'var(--color-text)', fontSize: '0.9rem' }}><option value="email_opened">Email ouvert</option><option value="email_clicked">Lien cliqué</option></select>
            </div>}

            {node.type === 'trigger' && <div style={{ padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></div>
                <span style={{ fontWeight: 600, color: '#10b981' }}>Début de la séquence</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: 0 }}>
                Ce node marque le point de départ du workflow. Quand un prospect est inscrit à cette séquence, l'exécution commence ici et passe au node suivant.
              </p>
            </div>}

            <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
               <button onClick={() => onSave(formData, node.id)} style={{ background: '#5b5cf4', color: '#fff', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>Sauvegarder</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
